import { WorkspaceRole } from '~api/workspaces/getWorkspaceMembers';
import { DEFAULT_WORKSPACE_SETTINGS } from '~api/workspaces/types';

import { useWorkspacePermissionsV2 } from '~teacher/hook/workspaces';
import { useGetWorkspaceSettings } from '~teacher/hook/workspaces/useGetWorkspaceSettings';

import { meetsRoleFloor } from './meetsRoleFloor';
import { useIsWorkspaceReadOnly } from './useIsWorkspaceReadOnly';

interface UseWorkspaceActionPermissionsParams {
  workspaceId: string;
}

/**
 * Combines role-based permissions (`useWorkspacePermissionsV2`) with
 * library-settings gates (`useGetWorkspaceSettings`) to produce per-action
 * booleans. Each flag is `true` only when BOTH layers allow the action.
 *
 * - Boolean settings (allowCopyToPersonal, allowCopyToLibrary,
 *   allowDownloadPrint, allowSocCode): apply to every role, Owner included —
 *   turning one off blocks the action for all members.
 * - Role-floor settings (allowMemberInvite): all roles checked via
 *   `meetsRoleFloor`; Owner naturally passes every floor.
 *
 * Use `permissions` pass-through for `showProBadge` / paywall gating (plan
 * tier check only, not affected by library settings).
 */
export function useWorkspaceActionPermissions({
  workspaceId,
}: UseWorkspaceActionPermissionsParams) {
  const {
    permissions,
    workspaceRole,
    roleQuery,
    isLoading: isPermissionsLoading,
  } = useWorkspacePermissionsV2({ workspaceId });

  const settingsQuery = useGetWorkspaceSettings({ workspaceId });
  const settings = settingsQuery.data ?? DEFAULT_WORKSPACE_SETTINGS;

  const isLoading = isPermissionsLoading || settingsQuery.isLoading;
  const isOwner = workspaceRole === WorkspaceRole.Owner;

  // Official (read-only) library: content editing is locked for everyone, all
  // roles, until an Admin/Owner turns it off. Single source of truth for the
  // rule lives in useIsWorkspaceReadOnly (mirrors the backend enforcement).
  const isReadOnly = useIsWorkspaceReadOnly(workspaceId);

  return {
    // Boolean settings apply to every role, Owner included.
    canCopyToPersonal:
      permissions.copyQuizToPersonal && settings.allowCopyToPersonal,
    canCopyToLibrary:
      permissions.copyToAnotherWorkspace && settings.allowCopyToLibrary,
    canDownloadQuiz: permissions.downloadQuiz && settings.allowDownloadPrint,
    // Answer-key export shares the single "Download quiz & answer key" flag.
    canExportAnswerKey: permissions.downloadQuiz && settings.allowDownloadPrint,
    canShareSocCode: settings.allowSocCode,

    // Quiz editing is role-based, but a read-only (Official) library locks it
    // for everyone until the setting is turned off.
    canEditQuiz: permissions.editQuiz && !isReadOnly,
    // Role-floor settings — Owner naturally passes every floor
    canInviteMembers: meetsRoleFloor(workspaceRole, settings.allowMemberInvite),
    canViewMemberList:
      isOwner ||
      workspaceRole === WorkspaceRole.Editor ||
      !settings.hideMemberList,

    // True when the library is Official/read-only — consumers gate other
    // content-mutating affordances (create/delete/move) on this.
    isReadOnly,

    // Pass-through for consumers that need plan-only gating (showProBadge)
    permissions,
    workspaceRole,
    roleQuery,
    isLoading,
  };
}
